<template>
  <div
    v-if="show"
    ref="root"
    class="list menu-shadow-box rounded-borders"
    data-testid="list"
    :tabindex="isRovingFocusEnabled ? -1 : undefined"
    @mousemove="onPointerMove"
  >
    <slot name="beforeOptions" />
    <slot
      name="list-body"
      :select="select"
    >
      <div
        :id="listId ?? undefined"
        ref="listBody"
        :aria-label="ariaLabel"
        :aria-labelledby="ariaLabelledby"
        :aria-multiselectable="multiple ? true : undefined"
        :aria-orientation="isGrid ? undefined : 'vertical'"
        class="list-body"
        data-testid="listBody"
        :role="isGrid ? 'grid' : 'listbox'"
      >
        <div
          v-for="(option, index) in options"
          :id="getOptionId(index)"
          :key="index"
          :ref="(el) => setOptionEl(el, index)"
          :aria-disabled="isOptionDisabled(option) ? 'true' : undefined"
          :aria-selected="
            isSelectableOption(option) ? isSelected(option) : undefined
          "
          class="outline-none"
          :class="{
            active: index === optionIndex,
            'active--keyboard': index === optionIndex && isKeyboardNavigation,
            disabled: isOptionDisabled(option),
          }"
          :data-testid="optionTestId(option, index)"
          :role="optionRole(option)"
          tabindex="-1"
          @click.left.stop.prevent="select(option)"
          @focusin="onOptionFocus(index)"
          @focusout="onOptionBlur(index)"
          @mousedown.prevent
          @mouseover="onOptionMouseover(index)"
        >
          <slot
            :active="index === optionIndex"
            :disabled="isOptionDisabled(option)"
            :is-focused="index === focusedIndex"
            :keyboard-active="index === optionIndex && isKeyboardNavigation"
            name="option"
            :option="option"
            :selected="isSelected(option)"
          >
            <div
              class="full-width list-option flex justify-between"
              :class="{
                active: index === optionIndex,
                'active--keyboard':
                  index === optionIndex && isKeyboardNavigation,
                disabled: isOptionDisabled(option),
                selected: isSelected(option),
              }"
            >
              {{ displayOption(option) }}
            </div>
          </slot>
        </div>
      </div>
    </slot>

    <slot
      v-if="loading"
      name="loading"
    >
      <div class="tw:flex tw:justify-center tw:p-3">
        <QSpinner />
      </div>
    </slot>

    <slot
      v-if="!options.length"
      name="empty"
    >
      <div>
        <div
          class="list-option flex justify-between"
          disabled
        >
          {{ $t("autocomplete_list.no_available_options") }}
        </div>
      </div>
    </slot>

    <slot name="afterOptions" />
  </div>
</template>

<script lang="ts">
export interface AutoCompleteOption {
  [key: string]: any;
  disabled?: boolean;
  isGroup?: boolean;
  notSelectable?: boolean;
  testId?: string;
}

export type GenericOption = AutoCompleteOption | string | number;

export function isOptionObject(
  option: GenericOption | null
): option is AutoCompleteOption {
  return typeof option === "object";
}
</script>

<script setup lang="ts" generic="T extends GenericOption">
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { isNil } from "lodash-es";
import { tabbable } from "tabbable";
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  useTemplateRef,
  watch,
} from "vue";

import { type Maybe, type Nullable } from "shared/types";

export interface AutocompleteListProps<OptionType> {
  ariaLabel?: string;
  ariaLabelledby?: string;
  /**
   * Leave `active` to the keyboard, for lists whose options draw their own
   * hover with CSS. Hovering then costs nothing, rather than re-rendering every
   * row for each one the pointer crosses, which a long list can't afford.
   */
  isActiveKeyboardOnly?: boolean;
  /**
   * Lay the options out as an ARIA grid, for rows that hold their own controls
   * (see useOptionRowActions). An option's descendants are presentational, so a
   * button inside one is folded into the option's name rather than exposed as a
   * button. Each row then renders its own cells, and the focusable
   * `role="gridcell"` among them is where the vertical arrows land. Needs
   * isRovingFocusEnabled.
   */
  isGrid?: boolean;
  /**
   * Let Tab close a single-select popup and carry on from the trigger, for an
   * owner that has nothing to Tab between inside it (see InputSelect). Off, the
   * trap holds Tab, which is what a standalone list — free to render its own
   * controls in a slot — needs.
   */
  closesOnTab?: boolean;
  /** Roving DOM focus + focus trap instead of the default active-descendant model. */
  isRovingFocusEnabled?: boolean;
  listId?: Nullable<string>;
  loading?: boolean;
  multiple?: boolean;
  optionLabel?: string | ((option: OptionType) => string) | null;
  optionProp?: string | null;
  options?: OptionType[];
  /**
   * The owner focuses its trigger on close (see InputSelect); the trap's own
   * return lands later and would undo it. Off, the trap restores focus itself.
   */
  ownerReturnsFocus?: boolean;
  /** Slotted search field the arrows rove to, passed in to avoid querying slotted DOM. */
  searchEl?: Nullable<HTMLElement>;
  selection?: OptionType | OptionType[] | null;
}

enum Direction {
  Forward,
  Backward,
}

const props = withDefaults(defineProps<AutocompleteListProps<T>>(), {
  ariaLabel: undefined,
  ariaLabelledby: undefined,
  listId: null,
  options: () => [],
  searchEl: null,
  selection: null,
  optionProp: null,
  optionLabel: null,
});

const show = defineModel<boolean>("show", {
  required: true,
});

const emit = defineEmits<{
  select: [T];
  /**
   * Tab is leaving a single-select popup. Fired before the browser moves focus,
   * so a listener that focuses the trigger has the Tab carry on from there.
   */
  tabOut: [];
}>();

const optionIndex = ref(-1);
const focusedIndex = ref(-1);
const isKeyboardNavigation = ref(false);
const root = ref<HTMLElement | null>(null);
const listBody = useTemplateRef<HTMLDivElement>("listBody");

const optionEls: Record<number, HTMLElement> = {};

function setOptionEl(
  el: Element | ComponentPublicInstance | null,
  index: number
): void {
  if (el instanceof HTMLElement) {
    optionEls[index] = el;
  } else {
    delete optionEls[index];
  }
}

// Gate the target rather than calling activate(): activate() no-ops until
// useFocusTrap's post-flush watcher has built the trap, so a list mounted
// already open would never be trapped.
const focusTrapTarget = computed<Nullable<HTMLElement>>(() =>
  props.isRovingFocusEnabled ? root.value : null
);

const { deactivate: deactivateFocusTrap } = useFocusTrap(focusTrapTarget, {
  allowOutsideClick: true,
  escapeDeactivates: false,
  fallbackFocus: () => root.value!,
  immediate: true,
  returnFocusOnDeactivate: !props.ownerReturnsFocus,
});

// Scrolling under a stationary cursor fires a mousemove with unchanged
// coordinates; only a real move hands control back to the mouse.
let lastPointerX = -1;
let lastPointerY = -1;

function onPointerMove(event: MouseEvent): void {
  if (event.clientX === lastPointerX && event.clientY === lastPointerY) {
    return;
  }

  lastPointerX = event.clientX;
  lastPointerY = event.clientY;
  isKeyboardNavigation.value = false;

  // The pointer's row is highlighted in CSS, so the keyboard's highlight is
  // dropped rather than followed.
  if (props.isActiveKeyboardOnly) {
    optionIndex.value = -1;
  }
}

function onOptionMouseover(index: number): void {
  // Ignore mouseovers synthesised by scrolling; onPointerMove clears the flag
  // for genuine hovers.
  if (props.isActiveKeyboardOnly || isKeyboardNavigation.value) {
    return;
  }

  optionIndex.value = index;
}

// Real DOM focus — unlike `active`, which the mouse also drives. focusin/out
// rather than focus/blur: a control a consumer rendered inside the row counts
// as the row being focused, and only the bubbling pair sees that.
function onOptionFocus(index: number): void {
  focusedIndex.value = index;
}

function onOptionBlur(index: number): void {
  if (focusedIndex.value === index) {
    focusedIndex.value = -1;
  }
}

// Removed rows emit no `blur`; rows are keyed by index — re-derive from the DOM.
function syncFocusedIndex(): void {
  const { activeElement } = document;

  if (!listBody.value || !(activeElement instanceof HTMLElement)) {
    focusedIndex.value = -1;

    return;
  }

  focusedIndex.value = Array.from(listBody.value.children).findIndex((row) =>
    row.contains(activeElement)
  );
}

function findValidSelection(
  startingIndex: number,
  direction: Direction
): number {
  const step = direction === Direction.Forward ? 1 : -1;
  let index = startingIndex + step;

  while (index >= 0 && index < props.options.length) {
    if (isFocusableOption(props.options[index])) {
      return index;
    }

    index += step;
  }

  // Nothing to move to: stay put — but `activate` starts from a sentinel outside
  // the list, and handing that back would highlight a row that doesn't exist.
  return startingIndex >= 0 && startingIndex < props.options.length
    ? startingIndex
    : -1;
}

function hide(): void {
  show.value = false;
}

function scrollToActive(): void {
  if (!root.value || !listBody.value) {
    return;
  }

  if (typeof listBody.value.scrollTo !== "function") {
    return;
  }

  const activeOption =
    optionIndex.value >= 0 ? optionEls[optionIndex.value] : undefined;

  if (activeOption) {
    const extraScroll = 2 * activeOption.offsetHeight;

    const scrollHeight =
      activeOption.offsetTop - listBody.value.clientHeight + extraScroll;

    listBody.value.scrollTo(0, scrollHeight);
  }
}

function getOptionId(index: number): Maybe<string> {
  return props.listId ? `${props.listId}-option-${index}` : undefined;
}

const activeOptionId = computed<Maybe<string>>(() => {
  if (optionIndex.value < 0) {
    return undefined;
  }

  return getOptionId(optionIndex.value);
});

function isOptionDisabled(option: T): boolean {
  return Boolean(isOptionObject(option) && option.disabled);
}

function isSelectableOption(option: T): boolean {
  if (!isOptionObject(option)) return true;

  return !option.isGroup && !option.notSelectable;
}

// A row the highlight and the roving focus are allowed to land on.
function isFocusableOption(option: T): boolean {
  return isSelectableOption(option) && !isOptionDisabled(option);
}

// `optionProp` is a stable id, so it survives a refetch where reference equality
// doesn't.
function isSameOption(option: T, other: T): boolean {
  if (isOptionObject(option) && isOptionObject(other) && props.optionProp) {
    return option[props.optionProp] === other[props.optionProp];
  }

  return option === other;
}

// `0` and `""` are valid options, so absence is `undefined` — not falsiness.
function indexOfOption(option: Maybe<T>): number {
  if (option === undefined) return -1;

  return props.options.findIndex((candidate) =>
    isSameOption(candidate, option)
  );
}

function optionTestId(option: T, index: number): string {
  return isOptionObject(option) && option?.testId
    ? option.testId
    : `listOption${index}`;
}

function select(option: T): void {
  if (isOptionDisabled(option) || !isSelectableOption(option)) {
    return;
  }

  emit("select", option);
}

// Called when the keyboard opens the list, so the edge option is highlighted on
// that same keypress per the ARIA combobox pattern.
function activate(position: "first" | "last"): void {
  isKeyboardNavigation.value = true;

  optionIndex.value = findValidSelection(
    position === "first" ? -1 : props.options.length,
    position === "first" ? Direction.Forward : Direction.Backward
  );

  nextTick(scrollToActive);
}

// The vertical set the arrows rove through: search field, then the options.
function getRovingItems(): HTMLElement[] {
  const options = props.options.flatMap((option, index) => {
    const el = optionEls[index];

    return el && isFocusableOption(option) ? [el] : [];
  });

  return props.searchEl ? [props.searchEl, ...options] : options;
}

function optionRole(option: T): string {
  if (props.isGrid) {
    return "row";
  }

  return isSelectableOption(option) ? "option" : "presentation";
}

// Where focus lands on a row. In a grid that is the row's focusable cell. The row
// stays focusable as a fallback for a row whose content hasn't rendered yet
// (StreamPicker renders rows lazily as they scroll into view).
function rovingTarget(item: HTMLElement): HTMLElement {
  if (!props.isGrid || item === props.searchEl) {
    return item;
  }

  return item.querySelector<HTMLElement>('[role="gridcell"][tabindex]') ?? item;
}

// The row holding focus on the row itself or its roving target — not on one of
// its actions. The row counts too: it takes focus as the fallback before its
// cell renders, and keeps it once the cell appears.
function focusedOption(): Maybe<HTMLElement> {
  const active = document.activeElement;

  return active instanceof HTMLElement
    ? Object.values(optionEls).find(
        (el) => el === active || rovingTarget(el) === active
      )
    : undefined;
}

// The row holding focus, whether that's the row itself or a control a consumer
// rendered inside it (see useOptionRowActions).
function focusedRow(): Maybe<HTMLElement> {
  const active = document.activeElement;

  if (!(active instanceof HTMLElement)) {
    return undefined;
  }

  return Object.values(optionEls).find(
    (el) => el === active || el.contains(active)
  );
}

// Elements are reused by index, so after a refresh the focused one may belong
// to a different option, or an unfocusable one that drops out of
// getRovingItems() and stops the arrows dead. Follow the option to its new row.
function restoreRovingFocus(previous: Maybe<T>, hadFocus: boolean): void {
  // Sampled before the DOM update: a refresh that removes the focused row
  // leaves focus on the list root, which here would read as never having had it.
  if (!hadFocus) {
    syncFocusedIndex();

    return;
  }

  const index = indexOfOption(previous);
  const option = index >= 0 ? props.options[index] : undefined;

  const row =
    option !== undefined && isFocusableOption(option) ? optionEls[index] : null;

  const target = row ? rovingTarget(row) : null;

  (target ?? props.searchEl ?? root.value)?.focus();
  syncFocusedIndex();
}

function ownsElement(target: Nullable<EventTarget>): boolean {
  if (!(target instanceof Node)) {
    return false;
  }

  return Boolean(
    root.value?.contains(target) || props.searchEl?.contains(target)
  );
}

// The popup holds a few things Tab can reach: the search field, and the clear
// button once you type. Is there another one ahead of where you are? Then stay
// open and let Tab move to it. If none are left, Tab is leaving, so the caller
// closes the list. Options can't be Tabbed to, so Tab from one always leaves.
function hasTabStopBeyond(
  target: Nullable<EventTarget>,
  isBackwards: boolean
): boolean {
  if (!root.value || !(target instanceof HTMLElement)) {
    return false;
  }

  const stops = tabbable(root.value);
  const index = stops.indexOf(target);

  if (index < 0) return false;

  return isBackwards ? index > 0 : index < stops.length - 1;
}

// On window so slotted controls are covered too, and in the capture phase so it
// runs ahead of the focus trap's own Tab handling on document.
function onRovingKeydown(event: KeyboardEvent): void {
  if (!props.isRovingFocusEnabled || !show.value) {
    return;
  }

  const { key } = event;

  if (key === "Tab") {
    // Only an owner that asks for it, and only single-select: multi-select's
    // confirm control is inside the popup, so closing would throw away the
    // unconfirmed selection.
    if (!props.closesOnTab || props.multiple || !ownsElement(event.target)) {
      return;
    }

    if (hasTabStopBeyond(event.target, event.shiftKey)) {
      return;
    }

    // A single-select popup has nothing left to Tab between, so Tab leaves. The
    // default action is left alone: the trap is released without returning
    // focus, tabOut lets the owner put focus back on the trigger, and the
    // browser's Tab then moves on from there in either direction.
    deactivateFocusTrap({ returnFocus: false });
    emit("tabOut");
    hide();

    return;
  }

  if (key === "Escape") {
    // Only the popup holding focus may swallow it — a sibling roving list would
    // close too. fallbackFocus keeps focus on `root`, so this always resolves.
    if (!ownsElement(event.target)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    hide();

    return;
  }

  if (key === "ArrowDown" || key === "ArrowUp") {
    const items = getRovingItems();
    const active = document.activeElement;

    // Resolved by containment so the vertical arrows still move row-to-row
    // while focus sits on one of a row's actions.
    const current =
      active instanceof HTMLElement
        ? items.findIndex((item) => item === active || item.contains(active))
        : -1;

    // Focus is outside the vertical list (e.g. confirm) — leave arrows alone.
    if (current === -1) {
      return;
    }

    event.preventDefault();

    const step = key === "ArrowDown" ? 1 : -1;
    const next = Math.min(Math.max(current + step, 0), items.length - 1);
    const item = items[next];

    if (item) {
      rovingTarget(item).focus();
    }

    return;
  }

  if (key === "Enter" || key === " ") {
    const option = focusedOption();

    if (option) {
      event.preventDefault();
      option.click();
    }
  }
}

function toggleRovingListener(method: "add" | "remove" = "add"): void {
  if (method === "add") {
    window.addEventListener("keydown", onRovingKeydown, true);
  } else {
    window.removeEventListener("keydown", onRovingKeydown, true);
  }
}

function handleKeypress(event: KeyboardEvent): void {
  const { key } = event;
  let shouldScrollToActive = false;

  if (key === "Tab") {
    hide();

    return;
  }

  if (!isListKeyboardEvent(event)) {
    return;
  }

  if (["ArrowDown", "ArrowUp"].includes(key)) {
    event.preventDefault();
    event.stopPropagation();
    isKeyboardNavigation.value = true;
    shouldScrollToActive = true;
  }

  if (key === "ArrowDown") {
    optionIndex.value = findValidSelection(
      optionIndex.value,
      Direction.Forward
    );
  }

  if (key === "ArrowUp") {
    optionIndex.value = findValidSelection(
      optionIndex.value,
      Direction.Backward
    );
  }

  if (key === "Enter") {
    if (props.options[optionIndex.value]) {
      event.preventDefault();
      event.stopPropagation();
      select(props.options[optionIndex.value]);
    }

    return;
  }

  if (key === "Escape") {
    // Escape closes the listbox only; letting it reach BaseInputText would blur the
    // combobox.
    event.preventDefault();
    event.stopPropagation();
    hide();

    return;
  }

  if (!props.options.length) {
    // Anything else publishes an aria-activedescendant id for an unrendered row.
    optionIndex.value = -1;
  } else if (optionIndex.value < 0) {
    optionIndex.value = 0;
  } else if (optionIndex.value > props.options.length - 1) {
    optionIndex.value = props.options.length - 1;
  }

  if (shouldScrollToActive) {
    nextTick(scrollToActive);
  }
}

function isListKeyboardEvent(event: KeyboardEvent): boolean {
  if (!root.value || !show.value) {
    return false;
  }

  if (!props.listId) return true;

  const { target } = event;

  if (!(target instanceof HTMLElement)) return false;

  if (root.value.contains(target)) return true;

  const controls = target.getAttribute("aria-controls");

  return Boolean(controls?.split(/\s+/).includes(props.listId));
}

function toggleKeyboardListener(method: "add" | "remove" = "add"): void {
  if (method === "add") {
    window.addEventListener("keydown", handleKeypress, true);
  } else {
    window.removeEventListener("keydown", handleKeypress, true);
  }
}

function displayOption(option: T): string {
  const isObject = isOptionObject(option);

  if (!isObject) {
    return option.toString();
  }

  if (props.optionLabel) {
    if (typeof props.optionLabel === "function") {
      return props.optionLabel(option);
    }

    return String(option[props.optionLabel] ?? "");
  }

  return "";
}

function isSelected(option: T): boolean {
  if (isNil(props.selection)) return false;

  const optionValue =
    isOptionObject(option) && props.optionProp
      ? option[props.optionProp]
      : option;

  if (!props.multiple || !Array.isArray(props.selection)) {
    return optionValue === props.selection;
  }

  return props.selection.some((opt) => {
    if (isOptionObject(opt) && props.optionProp) {
      return opt[props.optionProp] === optionValue;
    }

    if (!isOptionObject(opt)) {
      return opt === optionValue;
    }

    return JSON.stringify(opt) === JSON.stringify(option);
  });
}

async function scrollToSelected(): Promise<void> {
  if (!root.value || !listBody.value) return;

  if (typeof listBody.value.scrollTo !== "function") {
    return;
  }

  const selectedIndex = props.options.findIndex((option) => isSelected(option));

  const selectedOption =
    selectedIndex >= 0 ? optionEls[selectedIndex] : undefined;

  if (selectedOption) {
    await nextTick();

    const extraScroll = 2 * selectedOption.offsetHeight;

    const scrollHeight =
      selectedOption.offsetTop - listBody.value.clientHeight + extraScroll;

    listBody.value.scrollTo(0, scrollHeight);
  }
}

watch(
  show,
  (value) => {
    if (value) {
      optionIndex.value = -1;
      focusedIndex.value = -1;
      isKeyboardNavigation.value = false;

      if (!isNil(props.selection)) {
        nextTick(scrollToSelected);
      }
    }
  },
  { immediate: true }
);

watch(
  [show, () => props.isRovingFocusEnabled],
  ([value, isRovingFocusEnabled]) => {
    const isRoving = value && isRovingFocusEnabled;

    toggleRovingListener(isRoving ? "add" : "remove");
    toggleKeyboardListener(value && !isRovingFocusEnabled ? "add" : "remove");

    // useFocusTrap ignores an emptied target, and re-enabling overwrites its
    // handle — a trap left behind keeps Tab captive with nothing to release it.
    if (!isRoving) {
      deactivateFocusTrap();
    }
  },
  { immediate: true }
);

watch(
  () => props.options,
  (options, previousOptions) => {
    const active = previousOptions?.[optionIndex.value];
    const focused = previousOptions?.[focusedIndex.value];

    if (show.value && isKeyboardNavigation.value) {
      optionIndex.value = indexOfOption(active);

      return;
    }

    optionIndex.value = -1;

    if (show.value && props.isRovingFocusEnabled) {
      // Pre-update DOM; focusedRow() so a row's action counts as focus too.
      const hadFocus = Boolean(focusedRow());

      nextTick(() => restoreRovingFocus(focused, hadFocus));

      return;
    }

    nextTick(syncFocusedIndex);
  },
  { deep: true }
);

onBeforeUnmount(() => {
  toggleKeyboardListener("remove");
  toggleRovingListener("remove");
  deactivateFocusTrap();
});

defineExpose({
  activate,
  activeOptionId,
  handleKeypress,
  listBody,
  optionIndex,
});
</script>

<style lang="scss" scoped>
// Mouse focus only; `:focus-visible` below keeps the keyboard ring.
.outline-none:focus:not(:focus-visible) {
  outline: none;
}

// Drawn as a pseudo-element rather than an outline: a positioned slot wrapper
// (e.g. StreamPicker's QIntersection) paints its hover background over the
// option's own inset outline, but ::after paints after the children, so it
// stays on top whatever a consumer renders inside. `border` rather than
// `box-shadow` so the ring survives forced-colors mode.
[role="option"],
[role="row"] {
  position: relative;
}

// A row action tagged for reveal is an affordance rather than state: the
// pointer uncovers it by hovering, and — a keyboard having no hover — focus
// uncovers it too, on the row itself or on an action already inside it.
// `visibility` rather than `display`, so the row's label does not re-centre as
// it appears, and the action stays out of the tab order and the accessibility
// tree while hidden.
[role="option"] :deep([data-option-action-reveal]),
[role="row"] :deep([data-option-action-reveal]) {
  visibility: hidden;
}

[role="option"]:hover :deep([data-option-action-reveal]),
[role="option"]:focus-within :deep([data-option-action-reveal]),
[role="row"]:hover :deep([data-option-action-reveal]),
[role="row"]:focus-within :deep([data-option-action-reveal]) {
  visibility: visible;
}

// Replaced by the ring below; without this the UA default draws as well.
[role="option"]:focus-visible,
[role="row"]:focus-visible,
[role="row"] :deep([role="gridcell"]:focus-visible) {
  outline: none;
}

// In a grid the focus is usually on a cell, so the row draws the ring around
// itself for it, rather than just around that cell.
[role="option"]:focus-visible::after,
[role="row"]:focus-visible::after,
[role="row"]:has([role="gridcell"]:focus-visible)::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 2px solid var(--s-color-primary);
  pointer-events: none;
}

.list {
  background-color: var(--s-card-bg);

  // The real indicators live on the options.
  &:focus:not(:focus-visible) {
    outline: none;
  }

  // But as fallbackFocus it does hold keyboard focus when nothing inside is
  // tabbable, and that must stay visible.
  &:focus-visible {
    outline: 2px solid var(--s-color-primary);
    outline-offset: -2px;
  }

  .list-body {
    max-height: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    // a standalone listbox needs its own focus ring.
    &:focus-visible {
      outline: 2px solid var(--s-color-primary);
      outline-offset: -2px;
    }
  }
}

:deep(.list-option) {
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;

  &:hover {
    background: var(--s-color-denim-1);
  }

  &.active--keyboard,
  &:focus-visible {
    outline: 2px solid var(--s-color-primary);
    outline-offset: -2px;
  }

  &.group-option {
    padding: 8px;
    border-top: 1px solid var(--s-color-denim-2);
  }

  &.group {
    padding: 8px;
    font-weight: bold;
    pointer-events: none;
  }

  &.selected {
    background-color: var(--s-color-denim-2);
  }

  &.selected:not(:where(.active--keyboard)) {
    outline: none;
  }

  .list-option-action {
    color: $white;
  }
}
</style>
